/**
 * Bereitschaft und Kurznachrichten - der gemeinsame Teil des Warteraums
 * (Produktentscheidung vom 05.10.2026).
 *
 * Dieselben Bedienelemente stehen an zwei Stellen:
 * - im Warteraum vor dem Match,
 * - im Ergebnisbildschirm fuer die naechste Runde.
 *
 * Das ist Absicht. Vorher gab es zwei verschiedene "beide bereit?"-Abfragen
 * fuer dieselbe Frage; eine davon war eine zu viel.
 *
 * Es gibt KEIN Eingabefeld. Geschickt werden ausschliesslich die fuenf festen
 * Kurznachrichten, und zwar nur als Kennung - nie getippter Text. Damit kann
 * gar kein personenbezogener Inhalt entstehen (Masterprompt Abschnitt 27).
 */

import { LOBBY_SIGNALS } from '@dueline/shared';
import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';

interface Props {
  snapshot: MatchSnapshot;
  onToggleReady: () => void;
  onSignal: (signal: (typeof LOBBY_SIGNALS)[number]) => void;
}

export function LobbyControls({ snapshot, onToggleReady, onSignal }: Props) {
  const ichBereit = snapshot.lobbyReady;
  const gegnerBereit = snapshot.opponent?.lobbyReady ?? false;
  /*
   * Waehrend des Countdowns laesst sich die Zustimmung nicht mehr
   * zuruecknehmen - der Server lehnt sie ohnehin ab. Der Knopf wird deshalb
   * gesperrt, statt ins Leere zu klicken.
   */
  const laeuftSchon = snapshot.phase === 'starting';

  return (
    <>
      <div className="dl-lobby__state">
        <span className={`dl-lobby__flag${ichBereit ? ' dl-lobby__flag--on' : ''}`}>
          {ichBereit ? t.lobby.youReady : t.lobby.youNotReady}
        </span>
        <span className={`dl-lobby__flag${gegnerBereit ? ' dl-lobby__flag--on' : ''}`}>
          {gegnerBereit ? t.lobby.opponentReady : t.lobby.opponentNotReady}
        </span>
      </div>

      <button
        type="button"
        className={`dl-button ${ichBereit ? '' : 'dl-button--primary'} dl-lobby__ready`}
        onClick={onToggleReady}
        disabled={laeuftSchon}
      >
        {ichBereit ? t.lobby.readyCancel : t.lobby.ready}
      </button>

      <div className="dl-lobby__signals">
        {LOBBY_SIGNALS.map((signal) => (
          <button
            key={signal}
            type="button"
            className="dl-button dl-lobby__signal"
            onClick={() => onSignal(signal)}
          >
            {t.lobby.signals[signal]}
          </button>
        ))}
      </div>
    </>
  );
}

/**
 * Die zuletzt empfangene Kurznachricht.
 *
 * Immer nur die letzte, kein Verlauf: Das hier ist kein Chat zum Nachlesen,
 * sondern ein kurzes Zeichen. Die laufende Nummer im Schnappschuss sorgt
 * dafuer, dass auch dieselbe Nachricht zweimal hintereinander ankommt.
 */
export function LobbySignalLine({ snapshot }: { snapshot: MatchSnapshot }) {
  const zuletzt = snapshot.lobbySignal;
  if (!zuletzt) return null;

  return (
    <p className="dl-lobby__said" key={zuletzt.id}>
      <span className="dl-lobby__saidWho">
        {zuletzt.vonMir ? t.lobby.fromYou : t.lobby.fromOpponent}
      </span>{' '}
      {t.lobby.signals[zuletzt.signal]}
    </p>
  );
}
